import { useCallback, useEffect, useRef, useState } from "react";

// Names registered with the CSS Custom Highlight API. The matching
// `::highlight()` rules live in src/index.css.
const HIGHLIGHT_ALL = "dreamer-search";
const HIGHLIGHT_ACTIVE = "dreamer-search-active";

// Recompute is debounced so streaming responses / async grid renders don't
// thrash the TreeWalker on every DOM mutation.
const RECOMPUTE_DEBOUNCE_MS = 120;

// Upper bound on how many matches we paint in the dim "all matches" layer.
// A common short term in a long thread can match thousands of times; painting
// every one is wasteful and building the Highlight by spreading that many
// ranges can exceed the engine's argument limit. The true match count is still
// reported and the active match is always painted via its own highlight, so
// navigation across all matches is unaffected.
const MAX_PAINTED_HIGHLIGHTS = 2000;

// The CSS Custom Highlight API is not available in every browser; when it's
// missing the bar still works, it just doesn't paint highlights.
const supportsHighlightApi = (): boolean =>
  typeof CSS !== "undefined" &&
  "highlights" in CSS &&
  typeof Highlight !== "undefined";

const clearHighlights = () => {
  if (!supportsHighlightApi()) return;
  CSS.highlights.delete(HIGHLIGHT_ALL);
  CSS.highlights.delete(HIGHLIGHT_ACTIVE);
};

interface UseDreamerSearchArgs {
  containerRef: React.RefObject<HTMLElement>;
  isOpen: boolean;
}

export interface DreamerSearch {
  term: string;
  setTerm: (term: string) => void;
  clear: () => void;
  matchCount: number;
  /** 1-based position of the active match, or 0 when there are none. */
  activePosition: number;
  goToNext: () => void;
  goToPrevious: () => void;
}

/**
 * In-conversation find for the Dreamer thread. Walks the rendered message
 * container, highlights every case-insensitive occurrence of `term` (across
 * message text and grid cells alike) via the CSS Custom Highlight API, and
 * tracks/scrolls the active match.
 */
export const useDreamerSearch = ({
  containerRef,
  isOpen
}: UseDreamerSearchArgs): DreamerSearch => {
  const [term, setTerm] = useState("");
  const [matchCount, setMatchCount] = useState(0);
  const [activeIndex, setActiveIndex] = useState(0);
  // Bumped on every recompute so the active-match effect re-runs even when the
  // match count is unchanged but the underlying ranges moved — e.g. a streaming
  // reply rewrites a text node, leaving the same total count but stale ranges.
  const [matchGeneration, setMatchGeneration] = useState(0);
  const rangesRef = useRef<Range[]>([]);
  // Read inside computeMatches so the callback identity stays stable across
  // keystrokes; otherwise the MutationObserver below would tear down and
  // re-attach on every character typed, going briefly blind to DOM changes.
  const termRef = useRef(term);
  termRef.current = term;

  const computeMatches = useCallback(() => {
    const container = containerRef.current;
    const query = termRef.current.trim().toLowerCase();

    if (!container || !query) {
      rangesRef.current = [];
      clearHighlights();
      setMatchCount(0);
      setMatchGeneration((generation) => generation + 1);
      return;
    }

    const ranges: Range[] = [];
    const walker = document.createTreeWalker(container, NodeFilter.SHOW_TEXT, {
      acceptNode: (textNode) =>
        textNode.nodeValue && textNode.nodeValue.trim()
          ? NodeFilter.FILTER_ACCEPT
          : NodeFilter.FILTER_REJECT
    });

    let node = walker.nextNode();
    while (node) {
      const text = (node.nodeValue ?? "").toLowerCase();
      let from = text.indexOf(query);
      while (from !== -1) {
        const range = new Range();
        range.setStart(node, from);
        range.setEnd(node, from + query.length);
        ranges.push(range);
        from = text.indexOf(query, from + query.length);
      }
      node = walker.nextNode();
    }

    rangesRef.current = ranges;
    setMatchCount(ranges.length);
    setMatchGeneration((generation) => generation + 1);

    // Match counts and navigation work without the Highlight API; only the
    // visual paint depends on it. Build incrementally via .add() rather than
    // `new Highlight(...ranges)` — spreading a very large match set can exceed
    // the call argument limit and throw. Cap the painted layer; the true count
    // is reported above.
    if (!supportsHighlightApi()) return;
    const highlight = new Highlight();
    for (const range of ranges.slice(0, MAX_PAINTED_HIGHLIGHTS)) {
      highlight.add(range);
    }
    CSS.highlights.set(HIGHLIGHT_ALL, highlight);
  }, [containerRef]);

  // Reset to the first match whenever the term changes.
  useEffect(() => {
    setActiveIndex(0);
  }, [term]);

  // Clamp the active index when a recompute shrinks the result set, so the
  // counter can't report an impossible position (e.g. "5 of 2").
  useEffect(() => {
    setActiveIndex((index) =>
      matchCount === 0 ? 0 : Math.min(index, matchCount - 1)
    );
  }, [matchCount]);

  // Recompute whenever the term changes while the bar is open.
  useEffect(() => {
    if (!isOpen) return;
    computeMatches();
  }, [isOpen, term, computeMatches]);

  // Keep matches in sync with DOM changes (streaming replies, lazily-rendered
  // grids) while the bar is open. computeMatches has a stable identity, so the
  // observer is attached once per open rather than re-created on every keystroke.
  useEffect(() => {
    if (!isOpen) return;

    const container = containerRef.current;
    if (!container || typeof MutationObserver === "undefined") return;

    let timeout: ReturnType<typeof setTimeout> | undefined;
    const observer = new MutationObserver(() => {
      if (timeout) clearTimeout(timeout);
      timeout = setTimeout(computeMatches, RECOMPUTE_DEBOUNCE_MS);
    });
    observer.observe(container, {
      childList: true,
      subtree: true,
      characterData: true
    });

    return () => {
      if (timeout) clearTimeout(timeout);
      observer.disconnect();
    };
  }, [isOpen, computeMatches, containerRef]);

  // Paint and scroll the active match. Active highlight gets higher priority so
  // it paints over the dimmer "all matches" highlight where they overlap.
  // Keyed on matchGeneration too so a recompute that leaves the count unchanged
  // still re-points the active highlight at the fresh ranges.
  useEffect(() => {
    const ranges = rangesRef.current;
    const active = ranges[activeIndex];

    if (!active) {
      if (supportsHighlightApi()) CSS.highlights.delete(HIGHLIGHT_ACTIVE);
      return;
    }

    if (supportsHighlightApi()) {
      const activeHighlight = new Highlight(active);
      activeHighlight.priority = 1;
      CSS.highlights.set(HIGHLIGHT_ACTIVE, activeHighlight);
    }

    active.startContainer.parentElement?.scrollIntoView({
      block: "center",
      behavior: "smooth"
    });
  }, [activeIndex, matchCount, matchGeneration]);

  // Tear everything down when the bar closes (back arrow) or unmounts.
  useEffect(() => {
    if (isOpen) return;
    setTerm("");
    setMatchCount(0);
    rangesRef.current = [];
    clearHighlights();
  }, [isOpen]);

  useEffect(() => () => clearHighlights(), []);

  const goToNext = useCallback(() => {
    setActiveIndex((index) =>
      matchCount === 0 ? 0 : (index + 1) % matchCount
    );
  }, [matchCount]);

  const goToPrevious = useCallback(() => {
    setActiveIndex((index) =>
      matchCount === 0 ? 0 : (index - 1 + matchCount) % matchCount
    );
  }, [matchCount]);

  const clear = useCallback(() => setTerm(""), []);

  return {
    term,
    setTerm,
    clear,
    matchCount,
    activePosition: matchCount === 0 ? 0 : activeIndex + 1,
    goToNext,
    goToPrevious
  };
};
